import { useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Box, FormControlLabel } from '@mui/material';
import { styled } from '@mui/material/styles';
import useSWR from 'swr';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';
import Label from '@/components/@IntranetLibrary/Label';
import Modal, { type ModalRef } from '@/components/@IntranetLibrary/Modal';
import { RadioV2 } from '@/components/@IntranetLibrary/Radio/RadioV2';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { Body2 } from '@/components/@IntranetLibrary/Typography';
import { PLACEHOLDER_COLOR } from '@/components/@IntranetLibrary/DatePicker/styles';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import {
  buildClaimNoteHtml,
  ClaimNoteSnapshot,
  type ClaimNoteTarget,
} from '@/components/Approval/@components/ClaimNote';
import SearchableSelect, {
  type SearchableSelectOption,
} from '@/components/Approval/@components/SearchableSelect';
import type { ReplaceContentApi } from '@/components/LexicalEditor/plugins/ReplaceContentPlugin';
import { getClaimNoteDraftSourceUrl } from '@/constant/approval';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildClaimNoteEditorContent,
  buildClaimNoteLabel,
  buildClaimNoteSubject,
  CLAIM_NOTE_BLOCKED_MESSAGE,
  CLAIM_NOTE_EMPTY_EDITOR_CONTENT,
  CLAIM_NOTE_HOWLISTS,
  CLAIM_NOTE_RESET_MESSAGE,
  pickClaimNoteCard,
  type ClaimNoteCandidate,
  type ClaimNoteCard,
  type ClaimNoteClaimType,
  type ClaimNoteDraftSource,
  type ClaimNoteHowlist,
  type ClaimNoteItem,
} from '@/utils/approval/claimNote';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

const CONTROL_HEIGHT = 44;

type Props = { isEditMode: boolean; editorApi: ReplaceContentApi | null };

/**
 * GERP CLAIM NOTE(166) — 수입/수출 → 클레임 유형 → 내 미상신 건 여러 개 체크(건마다 카드).
 * 고르거나 뺄 때마다 제목·본문을 덮어씀. 한 문서는 한 유형 — 건을 고른 뒤 수입/수출·유형을 바꾸면 초기화 확인.
 * 편집은 건·유형을 바꿀 수 없고 저장 카드만
 */
const ClaimNoteField = ({ isEditMode, editorApi }: Props) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const dialog = useDialog();
  const { control, setValue } = useFormContext<ApprovalFormValues>();
  const saved = useWatch({ control, name: 'claimNote' });
  const addedModalRef = useRef<ModalRef>(null);

  const [howlist, setHowlist] = useState<ClaimNoteHowlist | ''>('');
  /** 고른 유형 이름(dtlCdNm) — 레거시 view_gubun */
  const [gubun, setGubun] = useState('');

  /** 고른 순서 그대로 */
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [cardsById, setCardsById] = useState<Record<string, ClaimNoteCard>>({});
  const [loadingIds, setLoadingIds] = useState<string[]>([]);
  // 카드 응답(비동기)이 최신 선택·카드를 보도록 state 와 같이 둠
  const selectedRef = useRef<string[]>([]);
  const cardsRef = useRef<Record<string, ClaimNoteCard>>({});
  /** 고른 건의 미상신 목록 정보·유형 — 유형을 바꿔도 남은 카드 제목을 그리려고 모아 둠 */
  const pickedRef = useRef<
    Record<string, { candidate: ClaimNoteCandidate; gubun: string }>
  >({});

  const onError = () =>
    snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });

  const { data: typeSource } = useSWR<ClaimNoteDraftSource>(
    isEditMode ? null : getClaimNoteDraftSourceUrl(),
    getNestedDataFetcher,
    { revalidateOnFocus: false, onError },
  );
  const claimTypes: ClaimNoteClaimType[] = typeSource?.display.claimTypes ?? [];

  const { data: listSource, isLoading } = useSWR<ClaimNoteDraftSource>(
    !isEditMode && howlist && gubun
      ? getClaimNoteDraftSourceUrl({ howlist, gubun })
      : null,
    getNestedDataFetcher,
    { revalidateOnFocus: false, onError },
  );

  const candidateById = useMemo(
    () =>
      new Map<string, ClaimNoteCandidate>(
        (listSource?.display.candidates ?? []).map((c) => [c.aprvId, c]),
      ),
    [listSource],
  );

  const labelOf = (id: string) => {
    const c = pickedRef.current[id]?.candidate ?? candidateById.get(id);
    return c ? buildClaimNoteLabel(c) : id;
  };

  const toItems = (
    ids: string[],
    loaded: Record<string, ClaimNoteCard>,
  ): ClaimNoteItem[] =>
    ids.flatMap((id) =>
      loaded[id]
        ? [
            {
              aprvId: id,
              label: labelOf(id),
              aprvRev:
                (
                  pickedRef.current[id]?.candidate ?? candidateById.get(id)
                )?.aprvRev?.trim() ?? '',
              card: loaded[id],
            },
          ]
        : [],
    );

  /** 카드를 다 받았을 때만 저장 값 — 덜 받았으면 상신 시 안내 */
  const syncSaved = (ids: string[], loaded: Record<string, ClaimNoteCard>) => {
    const first = pickedRef.current[ids[0] ?? ''];
    const isReady = !!first && ids.every((id) => loaded[id]);
    setValue(
      'claimNote',
      isReady
        ? {
            systemId: first.candidate.sysId,
            approvalTypeCode: first.candidate.aprvTypcd,
            approvalId: ids.join(','),
            claimType: first.gubun,
            content: buildClaimNoteHtml(toItems(ids, loaded), first.gubun),
          }
        : null,
      { shouldDirty: true },
    );
  };

  /** 레거시 change_title_content — 고르거나 뺄 때마다 제목·본문 덮어씀 */
  const overwriteSubjectAndBody = (ids: string[]) => {
    const labels = ids.map(labelOf);
    setValue('subject', buildClaimNoteSubject(labels, t), {
      shouldDirty: true,
    });
    editorApi?.replace(buildClaimNoteEditorContent(labels, t));
  };

  const update = (ids: string[], loaded: Record<string, ClaimNoteCard>) => {
    selectedRef.current = ids;
    cardsRef.current = loaded;
    setSelectedIds(ids);
    setCardsById(loaded);
  };

  const loadCard = async (id: string) => {
    const picked = pickedRef.current[id];
    if (!picked || !howlist) return;
    setLoadingIds((prev) => [...prev, id]);
    try {
      const data = await getNestedDataFetcher<ClaimNoteDraftSource>(
        getClaimNoteDraftSourceUrl({
          howlist,
          gubun: picked.gubun,
          approvalIds: [id],
        }),
      );
      const card = pickClaimNoteCard(data, id);
      if (!card) throw new Error('card missing');
      // 응답 전에 체크를 풀었으면 버림
      if (!selectedRef.current.includes(id)) return;
      const next = { ...cardsRef.current, [id]: card };
      update(selectedRef.current, next);
      syncSaved(selectedRef.current, next);
    } catch (error) {
      if (!selectedRef.current.includes(id)) return;
      // 같은 건을 다시 골라 재시도할 수 있게 선택에서 뺌
      const next = selectedRef.current.filter((k) => k !== id);
      update(next, cardsRef.current);
      overwriteSubjectAndBody(next);
      syncSaved(next, cardsRef.current);
      const isBlocked =
        axios.isAxiosError(error) && error.response?.status === 422;
      snackbar.showError({
        message: t(
          isBlocked
            ? CLAIM_NOTE_BLOCKED_MESSAGE
            : '데이터를 불러오지 못했습니다.',
        ),
      });
    } finally {
      setLoadingIds((prev) => prev.filter((k) => k !== id));
    }
  };

  const applySelection = (nextIds: string[]) => {
    const added = nextIds.filter((id) => !selectedRef.current.includes(id));
    added.forEach((id) => {
      const candidate = candidateById.get(id);
      if (candidate) pickedRef.current[id] = { candidate, gubun };
    });
    const loaded = { ...cardsRef.current };
    selectedRef.current
      .filter((id) => !nextIds.includes(id))
      .forEach((id) => delete loaded[id]);
    update(nextIds, loaded);
    overwriteSubjectAndBody(nextIds);
    syncSaved(nextIds, loaded);
    added.forEach(loadCard);
  };

  // 레거시 DelCost — 확인 없이 바로 뺌
  const handleDelete = ({ itemId }: ClaimNoteTarget) =>
    applySelection(selectedRef.current.filter((id) => id !== itemId));

  const resetAll = () => {
    update([], {});
    setValue('subject', '', { shouldDirty: true });
    editorApi?.replace(CLAIM_NOTE_EMPTY_EDITOR_CONTENT);
    setValue('claimNote', null, { shouldDirty: true });
  };

  /** 한 문서는 한 유형 — 고른 건이 있으면 초기화 확인 후 바꿈, 취소하면 그대로 */
  const confirmReset = (apply: () => void) => {
    if (selectedRef.current.length === 0) {
      apply();
      return;
    }
    dialog.show({
      message: t(CLAIM_NOTE_RESET_MESSAGE),
      confirmButtonText: t('확인'),
      cancelButtonText: t('취소'),
      onConfirm: () => {
        resetAll();
        apply();
      },
    });
  };

  /** 수입/수출을 바꾸면 유형을 다시 고름 */
  const handleHowlist = (next: ClaimNoteHowlist) => {
    if (next === howlist) return;
    confirmReset(() => {
      setHowlist(next);
      setGubun('');
    });
  };

  const handleClaimType = (type: ClaimNoteClaimType) => {
    if (type.dtlCdNm === gubun) return;
    confirmReset(() => {
      setGubun(type.dtlCdNm);
    });
  };

  if (isEditMode)
    return (
      <FieldBlock>
        {saved?.content && <ClaimNoteSnapshot content={saved.content} />}
      </FieldBlock>
    );

  const candidates = listSource?.display.candidates ?? [];
  const options: SearchableSelectOption[] = candidates.map((c) => {
    const label = buildClaimNoteLabel(c);
    return {
      value: c.aprvId,
      label,
      labelNode: (
        <OptionLabel>
          <Checkbox
            size="small"
            checked={selectedIds.includes(c.aprvId)}
            tabIndex={-1}
            sx={{ p: 0, pointerEvents: 'none' }}
          />
          {label}
        </OptionLabel>
      ),
    };
  });
  // 다른 유형·수입/수출에서 고른 건은 지금 목록에 없어 선택값에서 빼 두고 그대로 이어 붙임
  const optionIds = new Set(candidates.map((c) => c.aprvId));
  const visibleIds = selectedIds.filter((id) => optionIds.has(id));
  const placeholder = t('결재할 건을 선택해주세요');
  const items = toItems(selectedIds, cardsById);

  return (
    <FieldBlock>
      <Label size={14} isRequired={false} title={t('결재 유형')} />
      <RadioRow>
        {CLAIM_NOTE_HOWLISTS.map((h) => (
          <FormControlLabel
            key={h}
            label={t(h)}
            control={
              <RadioV2
                checked={howlist === h}
                onClick={() => handleHowlist(h)}
              />
            }
          />
        ))}
      </RadioRow>
      {howlist && claimTypes.length > 0 && (
        <RadioRow>
          {claimTypes.map((type) => (
            <FormControlLabel
              key={type.dtlCd}
              label={type.dtlCdNm}
              control={
                <RadioV2
                  checked={gubun === type.dtlCdNm}
                  onClick={() => handleClaimType(type)}
                />
              }
            />
          ))}
        </RadioRow>
      )}
      {gubun && (
        <ControlRow>
          <SearchableSelect
            multiple
            value={visibleIds}
            options={options}
            placeholder={isLoading ? t('불러오는 중...') : placeholder}
            noOptionsText={isLoading ? null : t('진행할 내역이 없습니다.')}
            // 레거시 콤보처럼 고른 건 대신 안내 문구 — 고른 건은 아래 카드
            renderValue={() => <Placeholder>{placeholder}</Placeholder>}
            onChange={(next) =>
              applySelection([
                ...selectedRef.current.filter(
                  (id) => !optionIds.has(id) || next.includes(id),
                ),
                ...next.filter((id) => !selectedRef.current.includes(id)),
              ])
            }
            sx={{
              ...approvalOutlineSelectSx,
              flex: 1,
              minWidth: 0,
              height: CONTROL_HEIGHT,
            }}
          />
          {loadingIds.length > 0 && <Spinner theme="black" size="16px" />}
          <BoxButton
            type="button"
            variant="secondary"
            size="medium"
            onClick={() => addedModalRef.current?.open()}
          >
            {t('추가 내역 확인')}
          </BoxButton>
        </ControlRow>
      )}
      {items.length > 0 && (
        <ClaimNoteSnapshot
          content={buildClaimNoteHtml(items, gubun)}
          onDelete={handleDelete}
        />
      )}
      <Modal
        ref={addedModalRef}
        title={t('추가 내역 확인')}
        size="x-small"
        handleClose={() => addedModalRef.current?.close()}
      >
        <AddedList>
          {selectedIds.map((id) => (
            <Body2 key={id} component="li">
              {labelOf(id)}
            </Body2>
          ))}
        </AddedList>
      </Modal>
    </FieldBlock>
  );
};

const RadioRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  columnGap: 24,
  marginBottom: 8,
  '& .MuiFormControlLabel-root': { margin: 0, gap: 8 },
  '& .MuiRadio-root': { padding: 0 },
});

const ControlRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
  [theme.breakpoints.down('md')]: {
    flexWrap: 'wrap',
    '& > .MuiInputBase-root, & > .MuiFormControl-root': { flexBasis: '100%' },
  },
}));

const OptionLabel = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const Placeholder = styled('span')({ color: PLACEHOLDER_COLOR });

const AddedList = styled('ul')({
  margin: 0,
  padding: 0,
  listStyle: 'none',
  '& > li + li': { marginTop: 4 },
});

export default ClaimNoteField;
